<template>
  <div :class="[
    'fixed top-0 left-0 z-[999] h-screen flex flex-col shadow-lg transition-all duration-300 ease-in-out will-change-transform overflow-hidden',
    {
      'w-[70px]': !isHovered && !isMobile,
      'w-[250px]': isHovered || isMobile,
      'translate-x-0': isSidebarVisible || !isMobile,
      '-translate-x-full': !isSidebarVisible && isMobile
    }
  ]"
  style="background-color: var(--p-toolbar-background);"
  @mouseenter="handleMouseEnter"
  @mouseleave="handleMouseLeave">
    <!-- Logo Section -->
    <div class="p-4 border-b border-gray-200 dark:border-gray-700 min-h-[80px] flex items-center flex-shrink-0">
      <div class="flex items-center gap-1 w-full transition-all duration-300 ease-in-out cursor-pointer" @click="goToDashboard">
        <img :src="logoSrc" alt="Logo" class="w-[55.5px] h-[47px] flex-shrink-0" />
        <div :class="['whitespace-nowrap transition-opacity duration-300 ease-in-out', { 'opacity-0': !isHovered && !isMobile }]">
          <svg viewBox="0 0 200 50" class="mt-[13px] w-full h-auto max-w-[120px]">
            <!-- Gradient -->
            <defs>
              <linearGradient class="font-bold text-xl" id="requests-gradient" x1="0%" y1="0%" x2="100%" y2="100%">
                <stop stop-color="#277e32" offset="0%"/>
                <stop stop-color="#4ade80" offset="50%"/>
                <stop stop-color="#3ab54b" offset="100%"/>
              </linearGradient>
            </defs>
            <!-- Text -->
            <text
              x="50%"
              y="50%"
              text-anchor="middle"
              dominant-baseline="middle"
              class="text-[2.5rem] font-extrabold font-inherit tracking-tight"
              fill="url(#requests-gradient)"
            >
              AH Requests
            </text>
          </svg>
        </div>
      </div>
    </div>

    <!-- Main Menu -->
    <div class="flex-1 py-2 overflow-y-auto overflow-x-hidden">
      <nav class="w-full">
        <ul class="list-none p-0 m-0">
          <li v-for="(item, index) in items" :key="index" v-show="item.visible">
            <!-- Regular menu item -->
            <router-link
              v-if="!item.hasSubmenu"
              :to="item.path"
              class="flex items-center px-5 py-4 text-gray-600 dark:text-gray-300 cursor-pointer no-underline relative whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out hover:bg-green-50 dark:hover:bg-green-900/20 active:bg-green-50 dark:active:bg-green-900/20 active:text-green-500 active:border-l-4 active:border-green-500 active:-ml-1 active:pl-[calc(1.25rem+3px)]"
              active-class="bg-green-50 dark:bg-green-900/20 text-green-500 border-l-4 border-green-500 -ml-1 pl-[calc(1.25rem+3px)]"
              style="border-left-color: transparent;"
            >
              <i :class="[item.icon, 'text-xl mr-3 min-w-5 text-center flex-shrink-0 opacity-100']"></i>
              <span :class="['text-base transition-opacity duration-300 ease-in-out whitespace-nowrap flex-1', { 'opacity-0': !isHovered && !isMobile }]">{{ item.label }}</span>
              <div v-if="!isHovered && !isMobile" class="absolute left-full top-1/2 -translate-y-1/2 -translate-x-[15px] scale-70 bg-gray-800 dark:bg-gray-700 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 invisible transition-all duration-400 ease-out z-[1000] ml-2 shadow-lg pointer-events-none before:content-[''] before:absolute before:top-1/2 before:-left-[5px] before:-translate-y-1/2 before:border-5 before:border-transparent before:border-r-gray-800 dark:before:border-r-gray-700 hover:opacity-100 hover:visible hover:translate-y-1/2 hover:translate-x-2 hover:scale-100 hover:delay-500">{{ item.label }}</div>
            </router-link>

            <!-- Collapsible menu item -->
            <div
              v-else
              class="flex items-center justify-between px-5 py-4 text-gray-600 dark:text-gray-300 cursor-pointer relative whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out hover:bg-green-50 dark:hover:bg-green-900/20"
              style="border-left-color: transparent;"
              @click="toggleSubmenu(item.key)"
            >
              <i :class="[item.icon, 'text-xl mr-3 min-w-5 text-center flex-shrink-0 opacity-100']"></i>
              <span :class="['text-base transition-opacity duration-300 ease-in-out whitespace-nowrap flex-1', { 'opacity-0': !isHovered && !isMobile }]">{{ item.label }}</span>
              <i :class="[
                'pi pi-chevron-down text-xs transition-transform duration-300 ease-in-out flex-shrink-0 opacity-100',
                { 'opacity-0': !isHovered && !isMobile, 'rotate-180': expandedMenus[item.key] }
              ]"></i>
              <div v-if="!isHovered && !isMobile" class="absolute left-full top-1/2 -translate-y-1/2 -translate-x-[15px] scale-70 bg-gray-800 dark:bg-gray-700 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 invisible transition-all duration-400 ease-out z-[1000] ml-2 shadow-lg pointer-events-none before:content-[''] before:absolute before:top-1/2 before:-left-[5px] before:-translate-y-1/2 before:border-5 before:border-transparent before:border-r-gray-800 dark:before:border-r-gray-700 hover:opacity-100 hover:visible hover:translate-y-1/2 hover:translate-x-2 hover:scale-100 hover:delay-500">{{ item.label }}</div>
            </div>

            <!-- Submenu -->
            <ul
              v-if="item.hasSubmenu"
              :class="[
                'list-none p-0 m-0 max-h-0 overflow-hidden transition-all duration-300 ease-in-out',
                { 'max-h-[300px]': expandedMenus[item.key] }
              ]"
            >
              <li v-for="subItem in item.submenu" :key="subItem.key" v-show="subItem.visible">
                <router-link :to="subItem.path" class="flex items-center px-5 py-4 text-gray-600 dark:text-gray-300 cursor-pointer no-underline relative whitespace-nowrap overflow-hidden transition-all duration-300 ease-in-out hover:bg-green-50 dark:hover:bg-green-900/20 active:bg-green-50 dark:active:bg-green-900/20 active:text-green-500 active:border-l-4 active:border-green-500 active:-ml-1 active:pl-[calc(1.25rem+3px)]" active-class="bg-green-50 dark:bg-green-900/20 text-green-500 border-l-4 border-green-500 -ml-1 pl-[calc(1.25rem+3px)]" style="border-left-color: transparent;">
                  <i :class="[subItem.icon, 'text-[17px] mr-3 min-w-5 text-center flex-shrink-0 opacity-100']"></i>
                  <span :class="['text-sm transition-opacity duration-300 ease-in-out whitespace-nowrap', { 'opacity-0': !isHovered && !isMobile }]">{{ subItem.label }}</span>
                  <div v-if="!isHovered && !isMobile" class="absolute left-full top-1/2 -translate-y-1/2 -translate-x-[15px] scale-70 bg-gray-800 dark:bg-gray-700 text-white px-3 py-2 rounded-lg text-sm whitespace-nowrap opacity-0 invisible transition-all duration-400 ease-out z-[1000] ml-2 shadow-lg pointer-events-none before:content-[''] before:absolute before:top-1/2 before:-left-[5px] before:-translate-y-1/2 before:border-5 before:border-transparent before:border-r-gray-800 dark:before:border-r-gray-700 hover:opacity-100 hover:visible hover:translate-y-1/2 hover:translate-x-2 hover:scale-100 hover:delay-500">{{ subItem.label }}</div>
                </router-link>
              </li>
            </ul>
          </li>
        </ul>
      </nav>
    </div>

    <!-- Bottom Actions Section -->
    <div class="p-4 border-t border-gray-200 dark:border-gray-700 flex flex-col gap-2 flex-shrink-0">
      <!-- Theme Toggle -->
      <Button
        icon="pi pi-sun"
        class="flex items-center justify-center w-full p-3 rounded-lg transition-all duration-300 ease-in-out text-xl hover:bg-gray-50 dark:hover:bg-gray-800"
        severity="secondary"
        text
        @click="toggleTheme"
        v-tooltip.right="!isHovered && !isMobile ? 'Toggle theme' : ''"
      />

      <!-- User Menu -->
      <Menu ref="userMenu" :model="userMenuItems" :popup="true" />
      <Button
        v-if="isLogged"
        @click="toggleUserMenu"
        text
        class="flex items-center gap-3 w-full p-3 rounded-lg transition-all duration-300 ease-in-out no-underline text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-gray-800 hover:no-underline hover:text-gray-600 dark:hover:text-gray-300"
        v-tooltip.right="!isHovered && !isMobile ? 'User menu' : ''"
      >
        <i class="pi pi-user text-xl flex-shrink-0"></i>
        <span :class="['text-sm whitespace-nowrap transition-opacity duration-300 ease-in-out overflow-hidden text-ellipsis', { 'opacity-0': !isHovered && !isMobile }]">
          {{ user.name || user.email }}
        </span>
      </Button>
    </div>
  </div>

  <!-- Mobile Toggle Button -->
  <Button
    v-if="isMobile"
    icon="pi pi-bars"
    severity="secondary"
    text
    @click="toggleSidebar"
    class="fixed top-4 left-4 z-[997] w-12 h-12 rounded-lg bg-white dark:bg-gray-900 shadow-lg dark:shadow-gray-800/50 flex items-center justify-center text-xl transition-all duration-300 ease-in-out hover:bg-gray-50 dark:hover:bg-gray-800 hover:shadow-xl dark:hover:shadow-gray-800/70"
    v-tooltip.bottom="isSidebarVisible ? 'Hide sidebar' : 'Show sidebar'"
  />

  <!-- Overlay for mobile devices only -->
  <div
    v-if="isMobile"
    :class="['fixed top-0 left-0 w-full h-full bg-black bg-opacity-40 z-[998] opacity-0 invisible transition-all duration-600 ease-out', { 'opacity-100 visible': isSidebarVisible }]"
    @click="toggleSidebar"
  ></div>
</template>

<script>
import { ref, computed, watch, onMounted, onBeforeUnmount } from 'vue';
import { useRouter } from 'vue-router';
import { useStore } from 'vuex';
import Button from 'primevue/button';
import Menu from 'primevue/menu';


export default {
  emits: ['toggle-sidebar'],
  components: { Button, Menu },
  props: {
    isSidebarVisible: {
      type: Boolean,
      default: true
    }
  },
  setup(props, { emit }) {
    const router = useRouter();
    const store = useStore();
    const isMobile = ref(window.innerWidth < 992);
    const isHovered = ref(false);
    const userMenu = ref(null);
    const expandedMenus = ref({});

    // Get user data from store
    const user = computed(() => store.state.users.user);
    const isLogged = computed(() => store.state.users.user.isLogged);

    const items = computed(() => {
      // icons links: https://primeng.org/icons
      const menu = [
        {
          label: 'Dashboard',
          icon: 'pi pi-fw pi-chart-bar',
          path: '/dashboard',
          visible: isLogged.value
        },
        {
          label: 'After Hours Requests',
          icon: 'pi pi-fw pi-phone',
          path: '/requests',
          visible: isLogged.value
        },
        {
          label: 'Users',
          icon: 'pi pi-fw pi-user-plus',
          path: '/users',
          visible: isLogged.value
        }
      ];

      return menu;
    });

    const logoSrc = computed(() => {
      return require('@/assets/logo_icon.svg'); // Keep icon file name for now
    });

    // User menu items
    const userMenuItems = computed(() => [
      {
        label: 'Account',
        icon: 'pi pi-user',
        command: () => router.push('/account')
      },
      {
        label: 'Logout',
        icon: 'pi pi-sign-out',
        command: () => logout()
      }
    ]);

    const toggleSubmenu = (menuKey) => {
      // Always toggle on click, regardless of hover state
      expandedMenus.value[menuKey] = !expandedMenus.value[menuKey];
    };

    const toggleUserMenu = (event) => {
      userMenu.value.toggle(event);
    };

    const logout = () => {
      store.commit('users/logoutUser');
      router.push('/');
    };

    const toggleTheme = () => {
      const currentTheme = document.documentElement.classList.contains("dark-mode") ? "dark" : "light";
      const newTheme = currentTheme === "dark" ? "light" : "dark";
      document.documentElement.classList.toggle("dark-mode");
      store.dispatch('changeTheme', newTheme);
    };

    const handleMouseEnter = () => {
      if (!isMobile.value) {
        isHovered.value = true;
      }
    };

    const handleMouseLeave = () => {
      if (!isMobile.value) {
        isHovered.value = false;
        // Don't close submenus on mouse leave - keep them open until clicked
      }
    };

    const toggleSidebar = () => {
      emit('toggle-sidebar');
    };

    const goToDashboard = () => {
      if (isLogged.value) {
        try {
          router.push('/dashboard');
        } catch (error) {
          console.error('Navigation error:', error);
          // Fallback navigation
          window.location.href = '/dashboard';
        }
      }
    };

    // Resize handler
    const handleResize = () => {
      const wasMobile = isMobile.value;
      isMobile.value = window.innerWidth < 992;

      // Reset hover state on mobile
      if (isMobile.value) {
        isHovered.value = false;
        // On mobile devices, keep submenus expanded by default
        expandedMenus.value['assets'] = true;
      } else {
        // On desktop, keep submenus in their current state when transitioning from mobile
        if (wasMobile) {
          // Don't change submenu state when transitioning from mobile to desktop
        }
      }

      // Update store state
      store.commit('modals/setSidebarMobile', isMobile.value);
    };

    onMounted(() => {
      window.addEventListener('resize', handleResize);
      // Initialize mobile state in store
      store.commit('modals/setSidebarMobile', isMobile.value);

      // On mobile devices, keep submenus expanded by default
      if (isMobile.value) {
        expandedMenus.value['assets'] = true;
      }
    });

    onBeforeUnmount(() => {
      window.removeEventListener('resize', handleResize);
    });

    return {
      items,
      logoSrc,
      isMobile,
      isHovered,
      user,
      isLogged,
      userMenu,
      userMenuItems,
      expandedMenus,
      handleMouseEnter,
      handleMouseLeave,
      toggleSidebar,
      goToDashboard,
      toggleUserMenu,
      toggleSubmenu,
      logout,
      toggleTheme
    };
  },
};
</script>

<style>
/* Mobile sidebar visibility */
@media (max-width: 991px) {
  .sidebar {
    width: 250px !important;
  }

  .sidebar-hidden {
    transform: translateX(-100%);
    transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  }

  .sidebar-visible {
    transform: translateX(0);
    transition: transform 0.6s cubic-bezier(0.23, 1, 0.32, 1);
  }

  /* On mobile devices submenus are always visible */
  .submenu-list {
    max-height: none !important;
    overflow: visible;
  }

  .submenu-list.submenu-expanded {
    max-height: none !important;
  }
}

/* Desktop - always visible */
@media (min-width: 992px) {
  .sidebar {
    transform: translateX(0) !important;
  }
}

/* Utility classes */
.cursor-pointer {
  cursor: pointer;
}

.opacity-0 {
  opacity: 0;
}
</style>
